import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, Typography, CircularProgress, Tabs, Tab } from "@mui/material";
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  PlayArrow as PlayArrowIcon,
  Language as LanguageIcon,
} from "@mui/icons-material";
import moment from "moment";
import { getInboxChatMediaItems, getInboxChatMediaUrl } from "api/whatsapp";
import { FONT, TEXT_DARK, TEXT_SECONDARY, TEXT_SUBTLE, BG_TINT, BORDER, PURPLE_MAIN, PURPLE_TINT_100 } from "components/bento/bentoTokens";

const TABS = ["media", "docs", "links"];
const URL_RE = /((?:https?:\/\/|www\.)[^\s<>]+)/i;
const emptyTab = () => ({ items: [], nextBefore: null, hasMore: true, loading: false, loaded: false });

const formatSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// "This month" / "Last month" / "September" / "September 2025", like WhatsApp's All media.
const monthLabel = (ts, t, lang) => {
  const d = moment.unix(ts);
  const now = moment();
  const diff = (now.year() - d.year()) * 12 + now.month() - d.month();
  if (diff <= 0) return t("whatsappChat.thisMonth");
  if (diff === 1) return t("whatsappChat.lastMonth");
  let month;
  try {
    month = d.toDate().toLocaleDateString(lang, { month: "long" });
  } catch (_) {
    month = d.format("MMMM");
  }
  return d.year() === now.year() ? month : `${month} ${d.year()}`;
};

const linkOf = (it) => {
  const raw = it.linkPreview?.url || it.body.match(URL_RE)?.[1] || "";
  return raw && !/^https?:\/\//i.test(raw) ? `https://${raw}` : raw;
};

// One request for the first page of a chat's photos/videos — used by the info panel's strip.
export const loadRecentMedia = (groupId, chatId, apiBase) =>
  getInboxChatMediaItems(groupId, chatId, "media", { apiBase }).then((r) => (r.success ? r.data.items : []));

/**
 * WhatsApp's "Media, links and docs" as a right-hand panel: Media (photo / video grid) · Docs ·
 * Links, newest first and grouped by month; scrolling down loads further back in the chat.
 * `onOpenGallery(slides, index)` opens the photo viewer on all loaded photos.
 */
const ChatMediaPanel = ({ groupId, apiBase = "settings", chat, onBack, onClose, onOpenGallery, t, lang }) => {
  const [tab, setTab] = useState("media");
  const [tabs, setTabs] = useState({ media: emptyTab(), docs: emptyTab(), links: emptyTab() });
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;

  useEffect(() => {
    setTabs({ media: emptyTab(), docs: emptyTab(), links: emptyTab() });
  }, [chat.id]);

  // A page can come back empty (nothing of that kind among the messages scanned) while older
  // history remains — keep going a few pages before giving up.
  const loadMore = useCallback(
    async (kind) => {
      const cur = tabsRef.current[kind];
      if (cur.loading || (cur.loaded && !cur.hasMore)) return;
      setTabs((s) => ({ ...s, [kind]: { ...s[kind], loading: true } }));
      let before = cur.nextBefore;
      let found = [];
      let hasMore = true;
      for (let round = 0; round < 4 && hasMore && found.length === 0; round++) {
        const r = await getInboxChatMediaItems(groupId, chat.id, kind, { before, apiBase });
        if (!r.success) {
          hasMore = false;
          break;
        }
        found = r.data.items || [];
        hasMore = !!r.data.hasMore && !!r.data.nextBefore;
        before = r.data.nextBefore;
      }
      setTabs((s) => {
        const ids = new Set(s[kind].items.map((x) => x.id));
        return {
          ...s,
          [kind]: { items: [...s[kind].items, ...found.filter((x) => !ids.has(x.id))], nextBefore: before, hasMore, loading: false, loaded: true },
        };
      });
    },
    [groupId, chat.id, apiBase]
  );

  useEffect(() => {
    if (!tabs[tab].loaded && !tabs[tab].loading) loadMore(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, tabs[tab].loaded]);

  const state = tabs[tab];
  const groups = useMemo(() => {
    const out = [];
    for (const it of state.items) {
      const label = monthLabel(it.timestamp, t, lang);
      if (!out.length || out[out.length - 1].label !== label) out.push({ label, items: [] });
      out[out.length - 1].items.push(it);
    }
    return out;
  }, [state.items, t, lang]);

  const urlOf = (it) => getInboxChatMediaUrl(groupId, chat.id, it.id, apiBase);
  const thumbOf = (it) => (it.thumbnail ? `data:image/jpeg;base64,${it.thumbnail}` : null);

  const openPhoto = (it) => {
    const photos = tabs.media.items.filter((x) => x.mediaType === "image");
    const index = Math.max(0, photos.findIndex((x) => x.id === it.id));
    onOpenGallery(
      photos.map((x) => ({ src: urlOf(x), fallback: thumbOf(x), filename: x.filename })),
      index
    );
  };

  return (
    <Box sx={{ width: 340, flexShrink: 0, display: "flex", flexDirection: "column", borderLeft: `1px solid ${BORDER}`, bgcolor: "#fff" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1.25, borderBottom: `1px solid ${BORDER}` }}>
        <IconButton size="small" color="secondary" onClick={onBack || onClose} aria-label={t("whatsappChat.back")}>
          {onBack ? <ArrowBackIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
        </IconButton>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 700, color: TEXT_DARK }}>{t("whatsappChat.mediaLinksDocs")}</Typography>
          <Typography noWrap sx={{ fontFamily: FONT, fontSize: 12, color: TEXT_SUBTLE }}>{chat.name}</Typography>
        </Box>
        {onBack && (
          <IconButton size="small" color="secondary" onClick={onClose} aria-label={t("common.close")}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth" textColor="secondary" indicatorColor="secondary" sx={{ borderBottom: `1px solid ${BORDER}`, minHeight: 42 }}>
        {TABS.map((k) => (
          <Tab key={k} value={k} label={t(`whatsappChat.mediaTabs.${k}`)} sx={{ fontFamily: FONT, textTransform: "none", fontWeight: 600, minHeight: 42 }} />
        ))}
      </Tabs>
      <Box
        sx={{ flex: 1, overflowY: "auto" }}
        onScroll={(e) => {
          const el = e.currentTarget;
          if (state.hasMore && el.scrollHeight - el.scrollTop - el.clientHeight < 200) loadMore(tab);
        }}
      >
        {groups.map((g) => (
          <Box key={`${tab}-${g.label}`}>
            <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, color: TEXT_SUBTLE, textTransform: "uppercase", letterSpacing: 0.6, px: 2, pt: 2, pb: 1 }}>
              {g.label}
            </Typography>
            {tab === "media" ? (
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "3px" }}>
                {g.items.map((it) => (
                  <Box
                    key={it.id}
                    onClick={() => (it.mediaType === "image" ? openPhoto(it) : window.open(urlOf(it), "_blank", "noopener"))}
                    sx={{ position: "relative", aspectRatio: "1 / 1", bgcolor: BG_TINT, cursor: "pointer", overflow: "hidden" }}
                  >
                    <Box
                      component="img"
                      src={it.mediaType === "image" ? urlOf(it) : thumbOf(it) || urlOf(it)}
                      alt=""
                      loading="lazy"
                      onError={(e) => {
                        const thumb = thumbOf(it);
                        if (thumb && e.currentTarget.src !== thumb) e.currentTarget.src = thumb;
                      }}
                      sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                    {it.mediaType === "video" && (
                      <Box sx={{ position: "absolute", left: 6, bottom: 6, width: 22, height: 22, borderRadius: "50%", bgcolor: "rgba(0,0,0,.55)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <PlayArrowIcon sx={{ fontSize: 16, color: "#fff" }} />
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            ) : tab === "docs" ? (
              g.items.map((it) => {
                const ext = ((it.filename || "").split(".").pop() || (it.mimetype || "").split("/").pop() || "").toUpperCase().slice(0, 4);
                return (
                  <Box
                    key={it.id}
                    component="a"
                    href={urlOf(it)}
                    target="_blank"
                    rel="noreferrer"
                    download={it.filename || undefined}
                    sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1, textDecoration: "none", "&:hover": { bgcolor: BG_TINT } }}
                  >
                    <Box sx={{ width: 40, height: 48, borderRadius: 1, bgcolor: PURPLE_TINT_100, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Typography sx={{ fontFamily: FONT, fontSize: 10.5, fontWeight: 800, color: PURPLE_MAIN }}>{ext || "DOC"}</Typography>
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontFamily: FONT, fontSize: 14, fontWeight: 600, color: TEXT_DARK, wordBreak: "break-word" }}>
                        {it.filename || t("whatsappChat.mediaLabels.document")}
                      </Typography>
                      <Typography sx={{ fontFamily: FONT, fontSize: 12, color: TEXT_SUBTLE }}>
                        {[formatSize(it.size), moment.unix(it.timestamp).format("DD/MM/YYYY")].filter(Boolean).join(" · ")}
                      </Typography>
                    </Box>
                  </Box>
                );
              })
            ) : (
              g.items.map((it) => {
                const url = linkOf(it);
                const p = it.linkPreview;
                return (
                  <Box
                    key={it.id}
                    component="a"
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1, textDecoration: "none", "&:hover": { bgcolor: BG_TINT } }}
                  >
                    {p?.thumbnail ? (
                      <Box component="img" src={`data:image/jpeg;base64,${p.thumbnail}`} alt="" sx={{ width: 52, height: 52, borderRadius: 1, objectFit: "cover", flexShrink: 0 }} />
                    ) : (
                      <Box sx={{ width: 52, height: 52, borderRadius: 1, bgcolor: PURPLE_TINT_100, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <LanguageIcon sx={{ color: PURPLE_MAIN }} />
                      </Box>
                    )}
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 600, color: TEXT_DARK, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", wordBreak: "break-word" }}>
                        {p?.title || url}
                      </Typography>
                      <Typography noWrap sx={{ fontFamily: FONT, fontSize: 12, color: "#1a73e8" }}>{url}</Typography>
                      <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: TEXT_SUBTLE }}>{moment.unix(it.timestamp).format("DD/MM/YYYY")}</Typography>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        ))}
        {state.loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={22} color="secondary" />
          </Box>
        )}
        {!state.loading && state.loaded && state.items.length === 0 && (
          <Typography sx={{ fontFamily: FONT, fontSize: 13, color: TEXT_SECONDARY, textAlign: "center", p: 4 }}>
            {t(`whatsappChat.mediaEmpty.${tab}`)}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ChatMediaPanel;
